'use client'

/**
 * Sign in and sign up (§62).
 *
 * Posts directly to the API from the browser so the `Set-Cookie` lands on the
 * user's own request — the session cookie is httpOnly, so the page never sees
 * the token, only its effect.
 *
 * After success it calls `router.refresh()` before navigating, so the server
 * components that render the header re-run and the page does not briefly show
 * a signed-out state to a signed-in user.
 */
import { useRouter } from 'next/navigation'
import { useId, useState } from 'react'

type Mode = 'signin' | 'signup'

/** Field name → message, so a 400 lands on the input that caused it. */
type FieldErrors = Partial<Record<'email' | 'username' | 'password', string>>

export function AuthForm({ mode, redirectTo }: { mode: Mode; redirectTo: string }) {
  const router = useRouter()
  const ids = useId()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<FieldErrors>({})
  const [busy, setBusy] = useState(false)

  const isSignup = mode === 'signup'

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setFields({})
    setBusy(true)

    try {
      const path = isSignup ? '/api/auth/register' : '/api/auth/login'
      const body = isSignup ? { email, username, password } : { email, password }

      const res = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as
          | { error?: { message?: string; details?: { path: string; message: string }[] } }
          | null

        /*
         * Zod's `details` carry the offending path, so a validation failure can be
         * attached to its own input instead of being flattened into one banner at
         * the top. The banner is kept for everything without a path — wrong
         * credentials, rate limiting, a duplicate email — because those are about
         * the attempt, not about a field.
         */
        const details = payload?.error?.details ?? []
        const mapped: FieldErrors = {}
        for (const d of details) {
          const key = d.path as keyof FieldErrors
          if (key === 'email' || key === 'username' || key === 'password') mapped[key] = d.message
        }

        if (Object.keys(mapped).length > 0) setFields(mapped)
        else setError(payload?.error?.message ?? 'Something went wrong. Try again.')

        setBusy(false)
        return
      }

      router.refresh()
      router.push(redirectTo)
    } catch {
      setError('Could not reach the server. Try again.')
      setBusy(false)
    }
  }

  return (
    <form className="auth-form" onSubmit={submit} noValidate>
      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}

      <div className={`field ${fields.email ? 'field-bad' : ''}`}>
        <label htmlFor={`${ids}-email`}>Email</label>
        <input
          id={`${ids}-email`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          /* `noValidate` on the form turns off the browser's own bubbles, which
             cannot be styled and disagree with our messages. `required` stays as
             the semantic hint for assistive tech; the API is the real check. */
          required
          autoFocus
          aria-invalid={fields.email ? true : undefined}
          aria-describedby={fields.email ? `${ids}-email-err` : undefined}
          placeholder="you@company.com"
        />
        {fields.email && (
          <small className="field-error" id={`${ids}-email-err`}>
            {fields.email}
          </small>
        )}
      </div>

      {isSignup && (
        <div className={`field ${fields.username ? 'field-bad' : ''}`}>
          <label htmlFor={`${ids}-username`}>Username</label>
          <div className="field-prefixed">
            <span className="field-prefix" aria-hidden="true">
              @
            </span>
            <input
              id={`${ids}-username`}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              autoComplete="username"
              required
              aria-invalid={fields.username ? true : undefined}
              aria-describedby={`${ids}-username-hint`}
              placeholder="yourname"
            />
          </div>
          <small className={fields.username ? 'field-error' : ''} id={`${ids}-username-hint`}>
            {fields.username ?? 'Letters, numbers, hyphens and underscores. This is how others see you.'}
          </small>
        </div>
      )}

      <div className={`field ${fields.password ? 'field-bad' : ''}`}>
        <label htmlFor={`${ids}-password`}>Password</label>
        <div className="field-prefixed">
          <input
            id={`${ids}-password`}
            type={reveal ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            required
            aria-invalid={fields.password ? true : undefined}
            aria-describedby={isSignup || fields.password ? `${ids}-password-hint` : undefined}
          />
          {/*
            A reveal toggle, because the alternative on a phone is retyping a long
            password after every typo — which is what actually pushes people
            towards short ones. `tabIndex={-1}` keeps it out of the tab path from
            password to submit.
          */}
          <button
            type="button"
            className="field-reveal"
            onClick={() => setReveal((v) => !v)}
            aria-pressed={reveal}
            aria-label={reveal ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {reveal ? 'Hide' : 'Show'}
          </button>
        </div>
        {(isSignup || fields.password) && (
          <small className={fields.password ? 'field-error' : ''} id={`${ids}-password-hint`}>
            {fields.password ?? 'At least 8 characters. Length beats symbols.'}
          </small>
        )}
      </div>

      <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
        {busy ? 'Please wait…' : isSignup ? 'Create account' : 'Sign in'}
      </button>
    </form>
  )
}
